// ============================================================================
// Canonical Task Center route contract
// ============================================================================
//
// TasksRouter owns a broad `/tasks/*` compatibility space. The Task Center
// shell and its To Do list are one surface reachable at four raw forms, but
// every other suffix is a separate, unreleased detail surface with its own
// renderer. This narrow, neutral predicate is shared by the resolver and the
// release boundary so adding the ledger row cannot accidentally authorize a
// task record, a To Do record, an alias, or a transition-frame path that the
// generic route table would canonicalize onto `/tasks`.
// ============================================================================

export const TASKS_CENTER_PATHNAME = '/tasks';
export const TASKS_CENTER_TRAILING_PATHNAME = '/tasks/';
export const TASKS_TODO_PATHNAME = '/tasks/todo';
export const TASKS_TODO_TRAILING_PATHNAME = '/tasks/todo/';

/** The four raw forms rendered by TasksRouter as `tasks.center`, in route order. */
export const CANONICAL_TASKS_CENTER_PATHNAMES: readonly string[] = Object.freeze([
  TASKS_CENTER_PATHNAME,
  TASKS_CENTER_TRAILING_PATHNAME,
  TASKS_TODO_PATHNAME,
  TASKS_TODO_TRAILING_PATHNAME,
]);

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

/**
 * The four routes rendered by TasksRouter as `tasks.center`. Search and hash
 * remain presentation state: TasksRouter reads `?task=` only to hand the
 * browser back to the still-legacy `/tasks/<id>` detail surface, and React
 * Router already carries them separately from pathname.
 */
export function isCanonicalTasksCenterPathname(pathname: unknown): pathname is string {
  return (
    pathname === TASKS_CENTER_PATHNAME ||
    pathname === TASKS_CENTER_TRAILING_PATHNAME ||
    pathname === TASKS_TODO_PATHNAME ||
    pathname === TASKS_TODO_TRAILING_PATHNAME
  );
}

/**
 * Prove the raw browser location before the resolver consults the ledger.
 * A bare string must be exactly a pathname; only a real location object may
 * carry the independently harmless search/hash presentation state.
 */
export function isCanonicalTasksCenterLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalTasksCenterPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;

  const {pathname, search, hash} = location as LocationLike;
  return (
    isCanonicalTasksCenterPathname(pathname) &&
    (search === undefined || typeof search === 'string') &&
    (hash === undefined || typeof hash === 'string')
  );
}
